import { CONSTANTS } from 'config/constants';

/**
 * Anatomical (dental chart, FDI notation) display order: the upper jaw read from the patient's
 * upper right (18) across to the upper left (28), then the lower jaw from the lower right (48)
 * across to the lower left (38). Teeth are listed in this order everywhere in Denthub (DD-3377).
 * The same helper exists in shared-denthub-code for the backend and the PDF services.
 */
export const TEETH_DISPLAY_ORDER: readonly string[] = [...CONSTANTS.UPPER_JAW, ...CONSTANTS.LOWER_JAW];

const DISPLAY_INDEX_BY_TOOTH = new Map<string, number>(
    TEETH_DISPLAY_ORDER.map((tooth, displayIndex) => [tooth, displayIndex]),
);

/** Position of a tooth in the chart order, or -1 for anything that is not an FDI tooth. */
const displayIndexOf = (tooth: number | string): number => DISPLAY_INDEX_BY_TOOTH.get(String(tooth).trim()) ?? -1;

export const isFdiTooth = (value: number | string): boolean => displayIndexOf(value) !== -1;

/**
 * Comparator for `Array.prototype.sort`. Teeth sort into chart order. Anything that is not a tooth
 * (a jaw label, a range string) ranks before every tooth and keeps its input order relative to other
 * non-tooth values, so a mixed list keeps the familiar "Upper jaw, 48, 41, 31" shape.
 */
export const compareTeeth = (left: number | string, right: number | string): number =>
    displayIndexOf(left) - displayIndexOf(right);

/** Non-mutating copy of `teeth` in chart order (see `compareTeeth`). */
export const sortTeeth = <T extends number | string>(teeth: readonly T[]): T[] => [...teeth].sort(compareTeeth);

/**
 * Human-readable list: non-tooth tokens first in their original order, then the teeth in chart order,
 * joined by `separator`. Empty tokens (from splitting an empty string) are dropped.
 */
export const formatTeethList = (teeth: readonly (number | string)[], separator = ', '): string =>
    sortTeeth(teeth.map((tooth) => String(tooth).trim()).filter((tooth) => tooth !== '')).join(separator);

/** Range string with its endpoints in chart order, e.g. `formatTeethRange(11, 15)` is `15 - 11`. */
export const formatTeethRange = (start: number, end: number): string => {
    const [first, last] = sortTeeth([start, end]);
    return `${first} - ${last}`;
};
